import { Page, View, Image, Text, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import {
  Portfolio,
  PublicEventDetailsResponse,
  ResponsePaginationType,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useTranslation } from 'react-i18next';
import { globalStyles } from '../../styles';
import { APP_HOST } from '@utils/apiHooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { SafeImage } from '../../components/SafeImage';
import { DEFAULT_TIMEZONE } from '@utils/constants';

dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

export interface IcebreakerDinnerPageProps {
  event: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  portfolios: ResponsePaginationType<Portfolio> | undefined;
  header: JSX.Element;
  footer: JSX.Element;
}

const icons = ['\uf57b', '\uf2e7']; // drink, dinner

const formatDateTime = (
  dateStr: string,
  language: string,
  timezone: string = DEFAULT_TIMEZONE
) => {
  const d = dayjs(dateStr);
  const isGerman = language.startsWith('de');

  return {
    date: isGerman
      ? d.format('D. MMMM YYYY') // 24. Juni 2024
      : d.format('MMMM D, YYYY'), // June 24, 2024
    time: isGerman
      ? d.format('HH:mm') // 24h → 20:00
      : d.format('hh:mm A'), // 12h → 8:00 PM
  };
};

const EventBlock = ({
  icon,
  title,
  subtitle,
  paragraphs,
  listItems,
  buttonLabel,
  imageSrc,
  date,
  time,
  locationName,
  locationAddress,
  event,
}: {
  icon: string;
  title: string;
  subtitle: string;
  paragraphs?: string[];
  listItems?: string[];
  buttonLabel: string;
  imageSrc: string;
  date: string;
  time: string;
  locationName: string;
  locationAddress: string;
  event: PublicEventDetailsResponse;
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  return (
    <View style={styles.itemWrapper}>
      {/* Row 1 */}
      <View style={styles.topRow}>
        <View
          style={{
            width: '25%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <View style={{ display: 'flex', flexDirection: 'row' }}>
            <Text style={styles.icon}>{icon}</Text>
            <Text style={styles.itemTitle}>{title}</Text>
          </View>

          <Text
            style={{
              ...styles.text,
              fontSize: 9,
              marginTop: 6,
              marginBottom: 4,
            }}
          >
            {subtitle}
          </Text>

          <View style={{ display: 'flex', flexDirection: 'row', gap: 20 }}>
            <Text style={styles.boldText}>{t('when')}:</Text>
            <View>
              <Text style={styles.text}>{date}</Text>
              <Text style={styles.text}>
                {time} {lang.startsWith('de') ? ` ${t('clock')}` : ''}
              </Text>
            </View>
          </View>

          <View style={{ display: 'flex', flexDirection: 'row', gap: 20 }}>
            <Text style={styles.boldText}>{t('where')}:</Text>
            <View>
              <Text style={styles.text}>{locationName}</Text>
              <Text style={styles.text}>{locationAddress}</Text>
            </View>
          </View>
        </View>

        <View style={{ width: '65%', maxWidth: 350 }}>
          <SafeImage
            style={styles.itemImage}
            src={imageSrc}
            debugLabel="icebreaker-sub-image"
          />
        </View>
      </View>

      {/* Row 2 */}
      <View style={styles.fullRow}>
        {paragraphs?.map((p, i) => (
          <Text key={i} style={styles.itemDescription}>
            {p}
          </Text>
        ))}
        {listItems?.map((item, i) => (
          <View
            key={i}
            style={{
              ...styles.itemListItem,
              display: 'flex',
              flexDirection: 'row',
              gap: 4,
            }}
          >
            <Text>{'\u2022'}</Text>
            <Text>{item}</Text>
          </View>
        ))}
      </View>

      {/* Row 3 */}
      <View style={styles.buttonWrapper}>
        <Link
          src={
            buttonLabel === t('icebreaker-button')
              ? `${APP_HOST}/events/${event?.slug}/icebreaker`
              : `${APP_HOST}/events/${event?.slug}/evening-networking`
          }
          style={styles.buttonText}
        >
          {buttonLabel}
        </Link>
      </View>
    </View>
  );
};

const IcebreakerDinnerPage = ({
  event,
  coLocatedEvents,
  portfolios,
  header,
  footer,
}: IcebreakerDinnerPageProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // Icebreaker details
  const startDateIcebreaker =
    (event?.website_content as any)?.onsite?.icebreaker?.start_date ||
    event?.start_date;
  const locationNameIcebreaker =
    (event?.website_content as any)?.onsite?.icebreaker?.location_name ||
    event?.location?.name ||
    '';
  const locationAddressIcebreaker =
    (event?.website_content as any)?.onsite?.icebreaker?.location_address ||
    `${event?.location?.name ?? ''}, ${event?.location?.marker?.street ?? ''}, ${event?.location?.marker?.postalCode ?? ''} ${event?.location?.marker?.city ?? ''}`;
  const icebreakerDateTime = formatDateTime(startDateIcebreaker, lang);

  // Dinner details
  const startDateDinner =
    (event?.website_content as any)?.onsite?.dinner?.start_date ||
    dayjs(event?.start_date)
      .add(1, 'day')
      .hour(20)
      .minute(0)
      .second(0)
      .millisecond(0)
      .toISOString();
  const locationNameDinner =
    (event?.website_content as any)?.onsite?.dinner?.location_name ||
    event?.location?.name ||
    '';
  const locationAddressDinner =
    (event?.website_content as any)?.onsite?.dinner?.location_address ||
    `${event?.location?.name ?? ''}, ${event?.location?.marker?.street ?? ''}, ${event?.location?.marker?.postalCode ?? ''} ${event?.location?.marker?.city ?? ''}`;
  const dinnerDateTime = formatDateTime(startDateDinner, lang);

  return (
    <Page size="A4" orientation="portrait" style={styles.page}>
      <View style={styles.backgroundContainer}>
        <SafeImage
          src="https://media.we-conect.com/images/pdf/agenda/pdf_agenda_icebreaker_hero.jpg"
          style={styles.backgroundImage}
          debugLabel="agenda-icebreaker-background"
        />
        <View>{header}</View>
        <View style={styles.overlayContent}>
          <Text style={styles.title}>{'Icebreaker & Dinner'}</Text>
          <View
            style={{
              ...globalStyles.lineBar,
              borderColor: 'white',
              marginLeft: 0,
              marginTop: 10,
            }}
          />
        </View>
      </View>

      <View style={styles.contentWrapper}>
        {/* Icebreaker */}
        <EventBlock
          icon={icons[0]}
          title={t('icebreaker')}
          subtitle={t('what-is-the-icebreaker-session')}
          paragraphs={[
            t('icebreaker-session-pdf-paragraph-1'),
            t('icebreaker-session-pdf-paragraph-2'),
            t('icebreaker-session-pdf-paragraph-3'),
          ]}
          buttonLabel={t('icebreaker-button')}
          imageSrc="https://media.we-conect.com/images/pdf/agenda/pdf_agenda_icebreaker_2.jpg"
          date={icebreakerDateTime.date}
          time={icebreakerDateTime.time}
          locationName={locationNameIcebreaker}
          locationAddress={locationAddressIcebreaker}
          event={event}
        />

        {/* Dinner */}
        <EventBlock
          icon={icons[1]}
          title={t('dinner')}
          subtitle={t('bits-bites-bubbles-is-the-place-for-you')}
          paragraphs={[t('dinner-session-pdf-paragraph')]}
          listItems={[
            t('dinner-session-pdf-list-item-1'),
            t('dinner-session-pdf-list-item-2'),
            t('dinner-session-pdf-list-item-3'),
          ]}
          buttonLabel={t('dinner-button')}
          imageSrc="https://media.we-conect.com/images/pdf/agenda/pdf_agenda_icebreaker_3.jpg"
          date={dinnerDateTime.date}
          time={dinnerDateTime.time}
          locationName={locationNameDinner}
          locationAddress={locationAddressDinner}
          event={event}
        />
      </View>

      {footer}
    </Page>
  );
};

export default IcebreakerDinnerPage;
